import React from 'react';
import PropTypes from 'prop-types';
import Actions from 'components/ui/general/Actions';
import Column from 'components/ui/show/Column';
import ComputedPriceField from 'components/show/ComputedPriceField';
import DateTimeTextField from 'components/show/DateFields/DateTimeTextField';
import DateTimeUTCTextField from 'components/show/DateFields/DateTimeUTCTextField';
import FullNameTextField from 'components/show/FullNameTextField';
import Header from 'components/ui/general/Header';
import InnerColumn from 'components/ui/show/InnerColumn';
import ProductShowField from 'components/show/ProductShowField';
import RefreshButton from 'components/button/RefreshButton';
import ReservationSessionActivitiesContainer from './session/ReservationSessionActivitiesContainer';
import ReservationSessionContainer from './session/ReservationSessionContainer';
import Section from 'components/ui/general/Section';
import TextField from 'components/show/TextField';
import Title from 'components/ui/general/Title';
import apiClient from 'clients/apiClient';
import auth from 'helpers/auth';
import config from 'helpers/config';
import categoryHelper from 'helpers/category';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { fetchAccessSessionActivitiesFailure, fetchAccessSessionActivitiesSuccess } from 'store/actions/reservations/session/reservationSessionActivities';
import translate from 'components/hoc/translate';
import ShowButton from 'components/button/ShowButton';
import ReservationOpenBarrierContainer from './open-barrier/ReservationOpenBarrierContainer';

const ReservationShow = (props) => {
    const {
        record,
        product,
        translate,
    } = props;

    const {
        booking_id = null,
        currency,
        cancelled_at,
        created_at,
        id,
        internal_order_id,
        policies: {
            session_end,
            session_start,
        },
        price,
        remote_reference = null,
        reservation_data,
        subscription_id,
        subscription_reservation_id,
        updated_at,
    } = record;

    const {
        customer_remarks,
        destination_address,
        destination_arrival_date,
        destination_departure_date,
        first_name,
        last_name,
        license_plate,
        number_of_persons,
        phone,
        return_flight_number,
    } = reservation_data;

    const categoryId = product.category_id;

    const isBikeProduct = categoryHelper.isBike(categoryId);
    const isHubProduct = categoryHelper.isHub(categoryId);
    const isParkingProduct = categoryHelper.isParking(categoryId);
    const isTaxiProduct = categoryHelper.isTaxi(categoryId);
    const isTransferProduct = categoryHelper.isTransfer(categoryId);
    const isAdmin = auth.isAdmin();

    const refreshAccessSessions = (callback) => {
        const {
            sendFailure,
            sendSuccess,
        } = props;

        const url = `${ config.getApiBaseUrl() }/reservations/${ id }/sessions`;

        return apiClient.read(url).then((response) => {
            sendSuccess({
                id: id,
                response: response,
            });
        }, (error) => {
            sendFailure(error);
        }).then(callback);
    };

    const bookingInformationAction = {
        url: `/bookings/${ booking_id }/show`,
        label: translate('filters.components.booking.label'),
    };

    if (subscription_id) {
        bookingInformationAction.url = `/subscriptions/${ subscription_id }/show`;
        bookingInformationAction.label = translate('resources.subscriptions.show.subscription');
    }

    return (
        <div>
            <Header>
                <Title title={ `${ translate('resources.reservations.name') }: ${ internal_order_id }` } />

                <Actions>
                    { isAdmin && (
                        <ReservationOpenBarrierContainer
                            id={ id }
                            product={ product }
                        />
                    )}
                    { isAdmin && (
                        <ReservationSessionContainer
                            id={ id }
                            canEndSession={ session_end.allowed }
                            canStartSession={ session_start.allowed }
                            product={ product }
                        />
                    ) }
                </Actions>
            </Header>

            <Section>
                <Column
                    size={ 8 }
                    title={ translate('resources.reservations.show.reservation_details') }
                >
                    <InnerColumn>
                        <FullNameTextField
                            data={ {
                                first_name: first_name,
                                last_name: last_name,
                            } }
                            label='resources.reservations.fields.full_name'
                        />

                        <TextField
                            label='resources.reservations.fields.phone'
                            value={ phone }
                        />

                        <TextField
                            label='resources.reservations.fields.customer_remarks'
                            value={ customer_remarks }
                        />
                    </InnerColumn>

                    <InnerColumn>
                        <TextField
                            label='resources.reservations.fields.license_plate'
                            value={ license_plate }
                        />

                        <TextField
                            label='resources.reservations.fields.return_flight_number'
                            value={ return_flight_number }
                        />
                    </InnerColumn>

                    { remote_reference && (
                        <InnerColumn>
                            <TextField
                                label='resources.reservations.fields.remote_reference'
                                value={ remote_reference }
                            />
                        </InnerColumn>
                    ) }
                </Column>
                { (currency && price) && (
                    <Column
                        size={ 4 }
                        title={ translate('resources.reservations.show.reservation_price') }
                    >
                        <InnerColumn>
                            <ComputedPriceField
                                currency={ currency }
                                label='fields.price'
                                value={ price }
                            />
                        </InnerColumn>
                    </Column>

                )}
            </Section>

            <Section>
                <Column
                    size={ 8 }
                    title={ translate('resources.reservations.show.travel_details') }
                >

                    <InnerColumn>
                        <DateTimeTextField
                            label={ (isTaxiProduct || isTransferProduct) ? 'resources.reservations.fields.pick_up_date' : 'resources.reservations.fields.starting_at' }
                            value={ destination_arrival_date }
                        />

                        { (isTaxiProduct || isTransferProduct) &&
                            <div>
                                {/* <TextField
                                    label='resources.reservations.fields.origin_address'
                                    value={ origin_address }
                                /> */}

                                <TextField
                                    label='resources.reservations.fields.destination_address'
                                    value={ destination_address }
                                />
                            </div>
                        }
                    </InnerColumn>

                    { (categoryId && (isBikeProduct || isHubProduct || isParkingProduct)) &&
                        <InnerColumn>
                            <DateTimeTextField
                                label='resources.reservations.fields.ending_at'
                                value={ destination_departure_date }
                            />
                        </InnerColumn>
                    }

                    <InnerColumn>
                        <TextField
                            label='resources.reservations.fields.number_of_persons'
                            value={ number_of_persons }
                        />
                    </InnerColumn>
                </Column>

                <Column
                    size={ 4 }
                    title={ translate('resources.reservations.show.product') }
                >
                    <InnerColumn>
                        <ProductShowField
                            hideLabel
                            product={ product }
                        />
                    </InnerColumn>
                </Column>
            </Section>

            <Section>
                <Column
                    size={ 12 }
                    title={ translate('resources.reservations.show.booking_information') }
                    actions={ isAdmin && (
                        <ShowButton
                            url={ bookingInformationAction.url }
                            label={ bookingInformationAction.label }
                        />
                    ) }
                >
                    <InnerColumn>
                        <DateTimeUTCTextField
                            label='resources.reservations.fields.created_at'
                            value={ created_at }
                        />
                    </InnerColumn>

                    <InnerColumn>
                        <DateTimeUTCTextField
                            label='resources.reservations.fields.updated_at'
                            value={ updated_at }
                        />
                    </InnerColumn>

                    <InnerColumn>
                        <DateTimeUTCTextField
                            label='resources.reservations.fields.cancelled_at'
                            value={ cancelled_at }
                        />
                    </InnerColumn>

                    { subscription_id && (
                        <InnerColumn>
                            <TextField
                                label='resources.reservations.fields.subscription_id'
                                value={ subscription_id }
                            />
                        </InnerColumn>
                    ) }

                    { subscription_reservation_id && (
                        <InnerColumn>
                            <TextField
                                label='resources.reservations.fields.subscription_reservation_id'
                                value={ subscription_reservation_id }
                            />
                        </InnerColumn>
                    ) }
                </Column>
            </Section>

            { (isAdmin && (isHubProduct || isBikeProduct)) &&
                <Section>
                    <Column
                        actions={ <RefreshButton id={ id } onClick={ refreshAccessSessions } /> }
                        size={ 12 }
                        title={ translate('resources.reservations.show.activities.access_sessions.title') }
                    >
                        <InnerColumn>
                            <ReservationSessionActivitiesContainer record={ record } />
                        </InnerColumn>
                    </Column>
                </Section>
            }
        </div>
    );
};

ReservationShow.propTypes = {
    product: PropTypes.shape({
        category_id: PropTypes.number.isRequired,
    }).isRequired,
    record: PropTypes.shape({
        booking_id: PropTypes.number,
        cancelled_at: PropTypes.string,
        created_at: PropTypes.string,
        currency: PropTypes.string,
        internal_order_id: PropTypes.number.isRequired,
        price: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.string,
        ]),
        product_id: PropTypes.string,
        id: PropTypes.number.isRequired,
        reservation_data: PropTypes.shape({
            customer_remarks: PropTypes.string,
            destination_address: PropTypes.string,
            destination_arrival_date: PropTypes.string,
            destination_departure_date: PropTypes.string,
            first_name: PropTypes.string,
            last_name: PropTypes.string,
            license_plate: PropTypes.string,
            number_of_persons: PropTypes.number,
            phone: PropTypes.string,
            return_flight_number: PropTypes.string,
        }),
        remote_reference: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.string,
        ]),
        subscription_id: PropTypes.number,
        subscription_reservation_id: PropTypes.number,
        policies: PropTypes.shape({
            session_end: PropTypes.shape({
                allowed: PropTypes.bool.isRequired,
            }).isRequired,
            session_start: PropTypes.shape({
                allowed: PropTypes.bool.isRequired,
            }).isRequired,
        }),
        updated_at: PropTypes.string,
    }).isRequired,
    sendFailure: PropTypes.func.isRequired,
    sendSuccess: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
};

const mapDispatchToProps = (dispatch) => {
    return {
        sendFailure: (error) => {
            dispatch(fetchAccessSessionActivitiesFailure(error));
        },
        sendSuccess: (response) => {
            dispatch(fetchAccessSessionActivitiesSuccess(response));
        },
    };
};

const enhance = compose(
    connect(
        null,
        mapDispatchToProps,
    ),
    translate,
);

export default enhance(ReservationShow);



// WEBPACK FOOTER //
// ./src/pages/reservations/show/ReservationsShow.js